import React, { useState, useEffect, useRef, useCallback } from 'react';
import './FieldSelectModule.css';
import { API_BASE_URL, getAuthHeaders } from '../utils/api';
import { 
  enhancedFetch, 
  generateErrorMessage, 
  
} from '../utils/errorHandling';

// Cache for field data to avoid redundant API calls
const fieldCache = {
  rawFields: null,
  // Deprecated: output fields endpoint removed; keep for backward-compatible cache shape
  outputFields: null,
  lastFetch: {
    rawFields: 0,
    outputFields: 0
  }
};

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

function FieldSelectModule({
  value,
  onChange,
  mode = 'single', // 'single' or 'multiple'
  fieldType = 'search', // 'search' or 'return'
  placeholder = 'Select field...',
  required = false,
  disabled = false,
  error = null,
  onError = null, // Callback for error reporting
  onClearError = null, // Callback for clearing errors
  // If provided, overrides internal fetching and uses this list of available fields
  fieldsOverride = null,
  // Optional: provide a list of file.* keys that will be converted to text when used for search
  fileKeysConvertToText = null,
  // Multiple mode: open a modal for large field sets (select all, fixed list height)
  useModalPicker = null,
  // Single mode: show a clear (×) control to empty the selection
  allowClear = false,
}) {
  const modalPickerEnabled = useModalPicker !== null ? useModalPicker : mode === 'multiple';
  const [isOpen, setIsOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [availableFields, setAvailableFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fieldError, setFieldError] = useState(error);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const onErrorRef = useRef(onError);
  const onClearErrorRef = useRef(onClearError);
  const fieldErrorRef = useRef(fieldError);
  const availableFieldsRef = useRef(availableFields);
  const fieldsOverrideRef = useRef(fieldsOverride);
  useEffect(() => {
    fieldsOverrideRef.current = fieldsOverride;
  }, [fieldsOverride]);

  // Update refs when props change
  useEffect(() => {
    onErrorRef.current = onError;
    onClearErrorRef.current = onClearError;
  }, [onError, onClearError]);

  // Keep a ref in sync with fieldError to avoid creating callback loops
  useEffect(() => {
    fieldErrorRef.current = fieldError;
  }, [fieldError]);

  // Keep a ref in sync with availableFields to avoid useCallback dependency loops
  useEffect(() => {
    availableFieldsRef.current = availableFields;
  }, [availableFields]);

  // Convert value to array for consistent handling
  const selectedValues = Array.isArray(value) ? value : (value ? [value] : []);

  // Enhanced fetch field data with comprehensive error handling
  const fetchFieldData = useCallback(async (forceRefresh = false) => {
    // If caller provides explicit fields, prefer them and skip fetching
    if (Array.isArray(fieldsOverride)) {
      const unique = [...new Set(fieldsOverride.filter(Boolean))].sort();
      const prev = availableFieldsRef.current || [];
      const isSameList = (
        unique.length === prev.length &&
        unique.every((v, i) => v === prev[i])
      );
      if (!isSameList) setAvailableFields(unique);
      setFieldError(null);
      fieldErrorRef.current = null;
      if (onClearErrorRef.current) {
        onClearErrorRef.current();
      }
      setLoading(false);
      return;
    }
    const now = Date.now();
    const needsRawFields = fieldType === 'search' || fieldType === 'return';
    // Deprecated: output fields endpoint will no longer return data; do not fetch
    const needsOutputFields = false;

    setLoading(true);
    setFieldError(null);
    
    // Clear parent error when starting fresh fetch
    if (onClearErrorRef.current) {
      onClearErrorRef.current();
    }

    try {
      const fields = [];

      // Fetch raw fields if needed
      if (needsRawFields) {
        const cacheValid = fieldCache.rawFields && 
          (now - fieldCache.lastFetch.rawFields) < CACHE_DURATION;
        
        if (!cacheValid || forceRefresh) {
          try {
            const authHeaders = await getAuthHeaders();
            const data = await enhancedFetch(`${API_BASE_URL}/casc/v1/api/data-entries`, {
              method: 'POST',
              headers: authHeaders,
              body: JSON.stringify({})
            }, {
              maxRetries: 2,
              baseDelay: 1000,
              errorType: 'network'
            });

            if (Array.isArray(fieldsOverrideRef.current)) {
              setLoading(false);
              return;
            }

            if (data.success && data.dataEntries) {
              const rawFields = data.dataEntries
                .map(entry => entry.column_name)
                .filter(Boolean)
                .map(field => `raw.${field}`);
              
              fieldCache.rawFields = rawFields;
              fieldCache.lastFetch.rawFields = now;
            } else {
              fieldCache.rawFields = [];
              throw new Error('Invalid response format: missing dataEntries');
            }
          } catch (error) {
            console.error('Error fetching raw fields:', error);
            fieldCache.rawFields = [];
            if (Array.isArray(fieldsOverrideRef.current)) {
              setLoading(false);
              return;
            }
            const errorMessage = generateErrorMessage(error, 'loading raw fields', error.response);
            const enhancedError = `Raw fields: ${errorMessage}`;
            setFieldError(enhancedError);
            
            if (onErrorRef.current) {
              onErrorRef.current(enhancedError);
            }
          }
        }

        if (Array.isArray(fieldsOverrideRef.current)) {
          setLoading(false);
          return;
        }

        if (fieldCache.rawFields) {
          fields.push(...fieldCache.rawFields);
        }
      }

      // Output fields endpoint deprecated: skip fetching and rely on raw fields

      // Remove duplicates and sort
      const uniqueFields = [...new Set(fields)].sort();
      // Only update available fields state if changed to prevent extra renders
      const prev = availableFieldsRef.current || [];
      const isSameList = (
        uniqueFields.length === prev.length &&
        uniqueFields.every((v, i) => v === prev[i])
      );
      if (!isSameList) {
        setAvailableFields(uniqueFields);
      }

      if (Array.isArray(fieldsOverrideRef.current)) {
        setLoading(false);
        return;
      }

      // If we have no fields at all, show appropriate error
      if (uniqueFields.length === 0 && !fieldErrorRef.current) {
        const noFieldsError = needsRawFields && needsOutputFields 
          ? 'No raw or output fields available. Please check your data configuration.'
          : needsRawFields 
            ? 'No raw fields available. Please check your data entries configuration.'
            : 'No output fields available.';
        
        if (fieldErrorRef.current !== noFieldsError) {
          setFieldError(noFieldsError);
          fieldErrorRef.current = noFieldsError;
        }
        if (onErrorRef.current) {
          onErrorRef.current(noFieldsError);
        }
      }

    } catch (error) {
      console.error('Error in fetchFieldData:', error);
      const errorMessage = generateErrorMessage(error, 'loading field data', error.response);
      if (fieldErrorRef.current !== errorMessage) {
        setFieldError(errorMessage);
        fieldErrorRef.current = errorMessage;
      }
      
      if (onErrorRef.current) {
        onErrorRef.current(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  }, [fieldType, fieldsOverride]);

  // Load field data when component mounts or dependencies change
  useEffect(() => {
    fetchFieldData();
  }, [fetchFieldData]);

  // Update error state when prop changes
  useEffect(() => {
    setFieldError(error);
    fieldErrorRef.current = error;
  }, [error]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!modalOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setModalOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [modalOpen]);

  // Filter fields based on search term
  const filteredFields = availableFields.filter(field =>
    field.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Build a quick lookup set for file keys that receive a conversion note
  const convertNoteSet = new Set(
    Array.isArray(fileKeysConvertToText) ? fileKeysConvertToText.filter(Boolean) : []
  );

  // Validate field selection
  const validateField = useCallback((fieldName) => {
    if (!fieldName) return null;

    // For search fields, allow same set as return fields: raw.*, file.*, output (no prefix), intrinsic.*
    if (fieldType === 'search') {
      // output keys have no special prefix; allow raw.*, file.*, intrinsic.* and output keys
      // Must be present in provided availableFields
      if (!availableFields.includes(fieldName)) {
        return 'Selected field is not available in the current data schema';
      }
    }

    // For return fields, allow both raw and output fields
    if (fieldType === 'return') {
      const isRawField = fieldName.startsWith('raw.');
      const isOutputField = !isRawField;
      
      if (isRawField && !availableFields.includes(fieldName)) {
        return 'Selected raw field is not available in the current data schema';
      }
      
      if (isOutputField && fieldCache.outputFields && !fieldCache.outputFields.includes(fieldName)) {
        // Allow custom output field names for return fields
        return null;
      }
    }

    return null;
  }, [fieldType, availableFields]);

  // Handle field selection with enhanced error handling
  const handleFieldSelect = (field) => {
    const validationError = validateField(field);
    
    if (validationError) {
      if (fieldErrorRef.current !== validationError) {
        setFieldError(validationError);
        fieldErrorRef.current = validationError;
      }
      if (onErrorRef.current) {
        onErrorRef.current(validationError);
      }
      return;
    }

    // Clear errors on successful selection
    if (fieldErrorRef.current) {
      setFieldError(null);
      fieldErrorRef.current = null;
    }
    if (onClearErrorRef.current) {
      onClearErrorRef.current();
    }

    if (mode === 'single') {
      onChange(field);
      setIsOpen(false);
      setSearchTerm('');
    } else {
      // Multiple mode
      const newValues = selectedValues.includes(field)
        ? selectedValues.filter(v => v !== field)
        : [...selectedValues, field];
      onChange(newValues);
    }
  };

  // Handle manual input (for custom field names)
  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter' && searchTerm.trim()) {
      e.preventDefault();
      const field = searchTerm.trim();
      // Do not auto-prefix in search mode; accept exact input so output/intrinsic/file/raw are all possible
      handleFieldSelect(field);
      setSearchTerm('');
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  // Handle removing selected field (multiple mode)
  const handleRemoveField = (fieldToRemove) => {
    const newValues = selectedValues.filter(v => v !== fieldToRemove);
    onChange(newValues);
  };

  const handleSelectAllFields = (fields) => {
    const target = Array.isArray(fields) ? fields.filter(Boolean) : [];
    if (target.length === 0) return;
    onChange([...new Set([...selectedValues, ...target])].sort());
    if (fieldErrorRef.current) {
      setFieldError(null);
      fieldErrorRef.current = null;
    }
    if (onClearErrorRef.current) onClearErrorRef.current();
  };

  const handleDeselectAllFields = () => {
    onChange([]);
  };

  const openModalPicker = () => {
    if (disabled) return;
    setModalOpen(true);
    setSearchTerm('');
    if (!availableFields.length && !loading) {
      fetchFieldData(true);
    }
  };

  const closeModalPicker = () => {
    setModalOpen(false);
    setSearchTerm('');
  };

  const renderFieldOption = (field) => (
    <button
      key={field}
      type="button"
      onClick={() => handleFieldSelect(field)}
      className={`field-select-option ${
        selectedValues.includes(field) ? 'selected' : ''
      }`}
    >
      <span className="field-select-option-name">{field}</span>
      {field.startsWith('raw.') && (
        <span className="field-select-option-type">Raw</span>
      )}
      {field.startsWith('file.') && (
        <span className="field-select-option-type">File</span>
      )}
      {field.startsWith('intrinsic.') && (
        <span className="field-select-option-type">Intrinsic</span>
      )}
      {!field.startsWith('raw.') && !field.startsWith('file.') && !field.startsWith('intrinsic.') && (
        <span className="field-select-option-type">Output</span>
      )}
      {fieldType === 'search' && field.startsWith('file.') && convertNoteSet.has(field) && (
        <span className="field-select-option-note">Will be converted to text for embedding</span>
      )}
      {selectedValues.includes(field) && (
        <i className="fas fa-check field-select-option-check"></i>
      )}
    </button>
  );

  const modalTitle = fieldType === 'return' ? 'Return fields' : 'Select fields';

  const handleClearSelection = () => {
    if (disabled) return;
    if (mode === 'single') {
      onChange('');
    } else {
      onChange([]);
    }
    setSearchTerm('');
    setFieldError(null);
    fieldErrorRef.current = null;
    if (onClearErrorRef.current) onClearErrorRef.current();
    setIsOpen(false);
  };

  // Refresh field data
  const handleRefresh = () => {
    fetchFieldData(true);
  };

  // Display value for input
  const getDisplayValue = () => {
    if (mode === 'single') {
      return isOpen ? searchTerm : (value || '');
    }
    return searchTerm;
  };

  return (
    <div className={`field-select-module ${disabled ? 'disabled' : ''}`} ref={dropdownRef}>
      <div className="field-select-input-container">
        {modalPickerEnabled ? (
          <div className="field-select-modal-trigger">
            <button
              type="button"
              className="field-select-modal-open-btn"
              onClick={openModalPicker}
              disabled={disabled}
            >
              <i className="fas fa-list-check" />
              {selectedValues.length > 0
                ? `${selectedValues.length} field${selectedValues.length === 1 ? '' : 's'} selected`
                : placeholder}
            </button>
            <div className="field-select-actions field-select-actions--inline">
              {loading && (
                <i className="fas fa-spinner fa-spin field-select-loading"></i>
              )}
              <button
                type="button"
                onClick={handleRefresh}
                disabled={disabled || loading}
                className="field-select-refresh"
                title="Refresh field data"
              >
                <i className="fas fa-sync-alt"></i>
              </button>
            </div>
          </div>
        ) : (
          <div className="field-select-input-wrapper">
            <input
              ref={inputRef}
              type="text"
              value={getDisplayValue()}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => !disabled && setIsOpen(true)}
              onKeyDown={handleInputKeyDown}
              placeholder={placeholder}
              disabled={disabled}
              className={`field-select-input ${fieldError ? 'error' : ''}`}
              required={required}
            />

            <div className="field-select-actions">
              {loading && (
                <i className="fas fa-spinner fa-spin field-select-loading"></i>
              )}
              {allowClear && mode === 'single' && Boolean(value) && (
                <button
                  type="button"
                  onClick={handleClearSelection}
                  disabled={disabled}
                  className="field-select-clear"
                  title="Clear search field"
                  aria-label="Clear search field"
                >
                  <i className="fas fa-times"></i>
                </button>
              )}
              <button
                type="button"
                onClick={handleRefresh}
                disabled={disabled || loading}
                className="field-select-refresh"
                title="Refresh field data"
              >
                <i className="fas fa-sync-alt"></i>
              </button>
              <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                disabled={disabled}
                className="field-select-toggle"
              >
                <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'}`}></i>
              </button>
            </div>
          </div>
        )}

        {/* Selected fields display (multiple mode) */}
        {mode === 'multiple' && selectedValues.length > 0 && (
          <div className={`field-select-selected ${modalPickerEnabled ? 'field-select-selected--compact' : ''}`}>
            {selectedValues.map((field) => (
              <span key={field} className="field-select-tag">
                {field}
                <button
                  type="button"
                  onClick={() => handleRemoveField(field)}
                  disabled={disabled}
                  className="field-select-tag-remove"
                >
                  <i className="fas fa-times"></i>
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Inline dropdown (single mode, or multiple with modal disabled) */}
      {isOpen && !disabled && !modalPickerEnabled && (
        <div className="field-select-dropdown">
          {loading ? (
            <div className="field-select-dropdown-loading">
              <i className="fas fa-spinner fa-spin"></i> Loading fields...
            </div>
          ) : filteredFields.length > 0 ? (
            <div className="field-select-options">
              {filteredFields.map((field) => renderFieldOption(field))}
            </div>
          ) : (
            <div className="field-select-dropdown-empty">
              {searchTerm ? (
                <div>
                  <p>No fields match "{searchTerm}"</p>
                  <small>Press Enter to add as custom field</small>
                </div>
              ) : (
                <p>No fields available</p>
              )}
            </div>
          )}
        </div>
      )}

      {modalOpen && !disabled && (
        <div
          className="field-select-modal-overlay"
          role="presentation"
        >
          <div
            className="field-select-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="field-select-modal-title"
          >
            <div className="field-select-modal-header">
              <h3 id="field-select-modal-title">{modalTitle}</h3>
              <button
                type="button"
                className="field-select-modal-close"
                onClick={closeModalPicker}
                aria-label="Close"
              >
                <i className="fas fa-times" />
              </button>
            </div>

            <div className="field-select-modal-toolbar">
              <input
                type="text"
                className="field-select-modal-search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Filter fields…"
                onKeyDown={handleInputKeyDown}
              />
              <div className="field-select-modal-bulk-actions">
                <button
                  type="button"
                  className="field-select-bulk-btn"
                  disabled={loading || availableFields.length === 0}
                  onClick={() => handleSelectAllFields(
                    searchTerm.trim() ? filteredFields : availableFields
                  )}
                >
                  Select {searchTerm.trim() ? 'visible' : 'all'}
                </button>
                <button
                  type="button"
                  className="field-select-bulk-btn"
                  disabled={selectedValues.length === 0}
                  onClick={handleDeselectAllFields}
                >
                  Deselect all
                </button>
              </div>
            </div>

            {mode === 'multiple' && selectedValues.length > 0 && (
              <div className="field-select-modal-selected-panel">
                <div className="field-select-modal-selected-label">
                  Selected ({selectedValues.length})
                </div>
                <div className="field-select-modal-selected-tags">
                  {selectedValues.map((field) => (
                    <span key={field} className="field-select-tag">
                      {field}
                      <button
                        type="button"
                        onClick={() => handleRemoveField(field)}
                        className="field-select-tag-remove"
                        aria-label={`Remove ${field}`}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="field-select-modal-list">
              {loading ? (
                <div className="field-select-dropdown-loading">
                  <i className="fas fa-spinner fa-spin"></i> Loading fields...
                </div>
              ) : filteredFields.length > 0 ? (
                <div className="field-select-options">
                  {filteredFields.map((field) => renderFieldOption(field))}
                </div>
              ) : (
                <div className="field-select-dropdown-empty">
                  {searchTerm ? (
                    <div>
                      <p>No fields match "{searchTerm}"</p>
                      <small>Press Enter to add as custom field</small>
                    </div>
                  ) : (
                    <p>No fields available</p>
                  )}
                </div>
              )}
            </div>

            <div className="field-select-modal-footer">
              <span className="field-select-modal-count">
                {selectedValues.length} of {availableFields.length} selected
              </span>
              <button
                type="button"
                className="field-select-modal-done"
                onClick={closeModalPicker}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error display */}
      {fieldError && (
        <div className="field-select-error">
          <i className="fas fa-exclamation-triangle"></i>
          {fieldError}
        </div>
      )}

      {/* Help text */}
      {!fieldError && (
        <div className="field-select-help">
          {fieldType === 'search' && 'Select a field for searching (raw, file, output, or intrinsic).'}
          {fieldType === 'return' && 'Select fields to return in results'}
          {mode === 'multiple' && ' (multiple selections allowed)'}
        </div>
      )}
    </div>
  );
}

export default FieldSelectModule;